import { useCallback, useState, type FormEvent, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import { IRB_STUDY_LABELS, LAB_NAME } from "../../api/labels";
import type {
  AdminAccessCode,
  AdminAccessCodesLoadResult,
  AdminCollaborator,
  AdminStudyIdResetLink,
  AdminStudyProtocol,
  AdminSubstudy,
  IrbStudy,
} from "../../api/types";
import { TabPanel } from "../Tabs";
import { ConsentVersionsCard } from "./ConsentVersionsCard";

// Substudies (create / onboard collaborators) + per-substudy
// protocol versions. The substudy list is shared shell state — creating a
// substudy must refresh every tab's dropdowns, hence the reloadSubstudies callback.
export function StudiesTab({
  substudies,
  reloadSubstudies,
}: {
  substudies: AdminSubstudy[];
  reloadSubstudies: () => Promise<void>;
}): ReactElement {
  // Substudies card: create a substudy, onboard a collaborator.
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newIrbStudy, setNewIrbStudy] = useState("");
  const [newOrigin, setNewOrigin] = useState("");
  const [collabSubstudy, setCollabSubstudy] = useState("");
  const [collabEmail, setCollabEmail] = useState("");
  const [studyBusy, setStudyBusy] = useState(false);
  const [studyError, setStudyError] = useState<string | null>(null);
  const [studyNotice, setStudyNotice] = useState<string | null>(null);
  // Set when an invite collides with an existing account: that 409 is exactly
  // what an admin hits when the first invite email never arrived, so the error
  // offers the resend rather than dead-ending.
  const [resendEmail, setResendEmail] = useState<string | null>(null);

  async function createSubstudy(e: FormEvent): Promise<void> {
    e.preventDefault();
    setStudyBusy(true);
    setStudyError(null);
    setStudyNotice(null);
    try {
      const t = await api<AdminSubstudy>("POST", "/admin/substudies", {
        json: {
          name: newName.trim(),
          slug: newSlug.trim(),
          irb_study: newIrbStudy,
          ...(newOrigin ? { origin: newOrigin } : {}),
        },
      });
      setNewName("");
      setNewSlug("");
      setNewIrbStudy("");
      setNewOrigin("");
      setStudyNotice(`Substudy "${t.name}" created. Load its study IDs below to open signup.`);
      await reloadSubstudies();
    } catch (err) {
      setStudyError(err instanceof ApiError ? err.message : "Could not create the substudy.");
    } finally {
      setStudyBusy(false);
    }
  }

  async function addCollaborator(e: FormEvent): Promise<void> {
    e.preventDefault();
    setStudyBusy(true);
    setStudyError(null);
    setStudyNotice(null);
    setResendEmail(null);
    const email = collabEmail.trim();
    try {
      const c = await api<AdminCollaborator>(
        "POST",
        `/admin/substudies/${collabSubstudy}/collaborators`,
        { json: { email } },
      );
      setCollabEmail("");
      setStudyNotice(
        `Invite sent to ${c.email}. The link in the email lets them set their own password (valid 7 days).`,
      );
    } catch (err) {
      setStudyError(err instanceof ApiError ? err.message : "Could not add the collaborator.");
      if (err instanceof ApiError && err.status === 409) setResendEmail(email);
    } finally {
      setStudyBusy(false);
    }
  }

  // Fresh 7-day token + a new email for a collaborator who never got (or let
  // lapse) their first invite. The API refuses if they already set a password.
  async function resendInvite(email: string): Promise<void> {
    setStudyBusy(true);
    setStudyError(null);
    setStudyNotice(null);
    try {
      const res = await api<{ detail: string }>("POST", "/admin/collaborators/resend-invite", {
        json: { email },
      });
      setResendEmail(null);
      setCollabEmail("");
      setStudyNotice(res.detail);
    } catch (err) {
      setStudyError(err instanceof ApiError ? err.message : "Could not resend the invite.");
      setResendEmail(null);
    } finally {
      setStudyBusy(false);
    }
  }

  // Study IDs card: pre-load study IDs + issue reset links for email-less
  // accounts. Study IDs are the ONLY signup path into a substudy (2026-08-14).
  const [codeSubstudy, setCodeSubstudy] = useState("");
  const [codeList, setCodeList] = useState<AdminAccessCode[]>([]);
  const [codePaste, setCodePaste] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeNotice, setCodeNotice] = useState<string | null>(null);
  const [resetLink, setResetLink] = useState<AdminStudyIdResetLink | null>(null);

  const loadCodes = useCallback(async (substudyId: string) => {
    if (!substudyId) {
      setCodeList([]);
      return;
    }
    try {
      setCodeList(await api<AdminAccessCode[]>("GET", `/admin/substudies/${substudyId}/access-codes`));
    } catch {
      setCodeList([]);
    }
  }, []);

  async function loadCodesBatch(e: FormEvent): Promise<void> {
    e.preventDefault();
    setCodeBusy(true);
    setCodeError(null);
    setCodeNotice(null);
    const codes = codePaste
      .split(/[\s,;]+/)
      .map((c) => c.trim())
      .filter(Boolean);
    if (codes.length === 0) {
      setCodeError("Paste at least one study ID.");
      setCodeBusy(false);
      return;
    }
    try {
      const res = await api<AdminAccessCodesLoadResult>(
        "POST",
        `/admin/substudies/${codeSubstudy}/access-codes`,
        { json: { codes } },
      );
      setCodePaste("");
      setCodeNotice(`${res.added} study ID(s) loaded, ${res.skipped} skipped (already present).`);
      await loadCodes(codeSubstudy);
    } catch (err) {
      setCodeError(err instanceof ApiError ? err.message : "Could not load the study IDs.");
    } finally {
      setCodeBusy(false);
    }
  }

  async function issueResetLink(userId: number): Promise<void> {
    setCodeBusy(true);
    setCodeError(null);
    setResetLink(null);
    try {
      setResetLink(await api<AdminStudyIdResetLink>("POST", `/admin/users/${userId}/password-reset`));
    } catch (err) {
      setCodeError(err instanceof ApiError ? err.message : "Could not create the reset link.");
    } finally {
      setCodeBusy(false);
    }
  }

  // Protocols card: publish/activate per-substudy portal configurations.
  const [protoSubstudy, setProtoSubstudy] = useState("");
  const [protoList, setProtoList] = useState<AdminStudyProtocol[]>([]);
  const [protoJson, setProtoJson] = useState("");
  const [protoBusy, setProtoBusy] = useState(false);
  const [protoError, setProtoError] = useState<string | null>(null);
  const [protoNotice, setProtoNotice] = useState<string | null>(null);

  const loadProtocols = useCallback(async (substudyId: string, prefillEditor = false) => {
    if (!substudyId) {
      setProtoList([]);
      return;
    }
    try {
      const list = await api<AdminStudyProtocol[]>("GET", `/admin/substudies/${substudyId}/protocols`);
      setProtoList(list);
      if (prefillEditor) {
        // Editing starts from the live config (versions are immutable — saving
        // always creates the next version). Falls back to the newest inactive
        // version, else an empty editor with the placeholder example.
        const base = list.find((p) => p.active) ?? list[0];
        setProtoJson(base ? JSON.stringify(base.config, null, 2) : "");
      }
    } catch {
      setProtoList([]);
    }
  }, []);

  async function saveProtocol(e: FormEvent): Promise<void> {
    e.preventDefault();
    setProtoBusy(true);
    setProtoError(null);
    setProtoNotice(null);
    let config: unknown;
    try {
      config = JSON.parse(protoJson);
    } catch {
      setProtoError("The config is not valid JSON.");
      setProtoBusy(false);
      return;
    }
    try {
      const p = await api<AdminStudyProtocol>("POST", `/admin/substudies/${protoSubstudy}/protocols`, {
        json: { config },
      });
      setProtoNotice(`Protocol v${p.version} saved (inactive). Activate it to take effect.`);
      await loadProtocols(protoSubstudy);
    } catch (err) {
      setProtoError(err instanceof ApiError ? err.message : "Could not save the protocol.");
    } finally {
      setProtoBusy(false);
    }
  }

  async function activateProtocol(protocolId: number): Promise<void> {
    setProtoBusy(true);
    setProtoError(null);
    setProtoNotice(null);
    try {
      const p = await api<AdminStudyProtocol>("POST", `/admin/protocols/${protocolId}/activate`);
      setProtoNotice(`Protocol v${p.version} is now live for the substudy.`);
      await loadProtocols(protoSubstudy);
    } catch (err) {
      setProtoError(err instanceof ApiError ? err.message : "Could not activate the protocol.");
    } finally {
      setProtoBusy(false);
    }
  }

  return (
    <TabPanel id="studies" card={false}>
      <section className="app-card">
        <h2>Substudies</h2>
        <p className="muted">
          A substudy runs under one of the IRB studies. Create the substudy, then load its
          study IDs in the Study IDs card below. Participants sign up at{" "}
          <code>{window.location.origin}/join/&lt;slug&gt;</code> (or the generic{" "}
          <code>/register/study-id</code>) with the personal ID their study team gave them.
          There is no shared study code and no email path into a substudy. Internal substudies
          can also get collaborator logins for their console.
        </p>
        {substudies.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Substudy</th>
                <th>Slug</th>
                <th>IRB study</th>
              </tr>
            </thead>
            <tbody>
              {substudies.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{t.slug}</td>
                  <td>{IRB_STUDY_LABELS[t.irb_study]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No substudies yet.</p>
        )}
        <h3>Create a Substudy</h3>
        <form className="q-form" onSubmit={createSubstudy}>
          <div className="field-grid">
            <label className="field">
              <span>Name</span>
              <input
                required
                value={newName}
                maxLength={200}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Acme Aging Study"
              />
            </label>
            <label className="field">
              <span>Slug (lowercase letters, digits, hyphens)</span>
              <input
                required
                value={newSlug}
                maxLength={64}
                pattern="[a-z0-9][a-z0-9-]*"
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="acme-aging"
              />
            </label>
            <label className="field">
              <span>Parent IRB study</span>
              <select
                required
                value={newIrbStudy}
                onChange={(e) => setNewIrbStudy(e.target.value)}
              >
                <option value="">Select…</option>
                {(Object.entries(IRB_STUDY_LABELS) as [IrbStudy, string][]).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Origin (optional)</span>
              <select value={newOrigin} onChange={(e) => setNewOrigin(e.target.value)}>
                <option value="">None</option>
                <option value="aim_mak_lab">{LAB_NAME}</option>
                <option value="external">External collaborator</option>
              </select>
            </label>
          </div>
          <p>
            <button className="btn-ghost btn-sm" disabled={studyBusy} type="submit">
              Create substudy
            </button>
          </p>
        </form>
        <h3>Invite a Collaborator</h3>
        <p className="muted">
          The collaborator receives an email invitation with a link to set their own
          password; no passwords change hands.
        </p>
        <form className="q-form" onSubmit={addCollaborator}>
          <div className="field-grid">
            <label className="field">
              <span>Substudy</span>
              <select
                required
                value={collabSubstudy}
                onChange={(e) => setCollabSubstudy(e.target.value)}
              >
                <option value="">Select…</option>
                {substudies.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                required
                value={collabEmail}
                onChange={(e) => setCollabEmail(e.target.value)}
                placeholder="pi@acme.org"
              />
            </label>
          </div>
          <p>
            <button
              className="btn-ghost btn-sm"
              disabled={studyBusy || !collabSubstudy}
              type="submit"
            >
              Send invite
            </button>
          </p>
        </form>
        {studyNotice ? <p className="muted">{studyNotice}</p> : null}
        {studyError ? <p className="form-error">{studyError}</p> : null}
        {resendEmail ? (
          <p>
            <button
              className="btn-ghost btn-sm"
              disabled={studyBusy}
              type="button"
              onClick={() => void resendInvite(resendEmail)}
            >
              Resend invite to {resendEmail}
            </button>
          </p>
        ) : null}
      </section>

      <section className="app-card">
        <h2>Study IDs</h2>
        <p className="muted">
          Every substudy assigns its participants personal study IDs. Load the study's ID list
          here. Participants then sign up at{" "}
          <code>{window.location.origin}/register/study-id</code> with their ID and a password,
          giving no email or name. With no email on file, password resets are issued here and
          handed to the study coordinator.
        </p>
        <div className="field-grid">
          <label className="field">
            <span>Substudy to load IDs for</span>
            <select
              value={codeSubstudy}
              onChange={(e) => {
                setCodeSubstudy(e.target.value);
                setResetLink(null);
                void loadCodes(e.target.value);
              }}
            >
              <option value="">Select…</option>
              {substudies.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </label>
        </div>
        {codeSubstudy ? (
          <>
            <form className="q-form" onSubmit={loadCodesBatch}>
              <label className="field">
                <span>Paste study IDs (one per line, or comma-separated)</span>
                <textarea
                  rows={4}
                  value={codePaste}
                  onChange={(e) => setCodePaste(e.target.value)}
                  placeholder={"NA-0001\nNA-0002\nNA-0003"}
                />
              </label>
              <p>
                <button className="btn-ghost btn-sm" disabled={codeBusy} type="submit">
                  Load study IDs
                </button>
              </p>
            </form>
            {codeNotice ? <p className="muted">{codeNotice}</p> : null}
            {codeError ? <p className="form-error">{codeError}</p> : null}
            {resetLink ? (
              <p className="form-ok">
                Reset link (valid until {resetLink.expires_at.slice(0, 10)}). Copy it now; it is
                not shown again): <code>{resetLink.reset_link}</code>
              </p>
            ) : null}
            {codeList.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Study ID</th>
                    <th>Status</th>
                    <th>Participant</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {codeList.map((c) => (
                    <tr key={c.code}>
                      <td>
                        <code>{c.code}</code>
                      </td>
                      <td>{c.used_at ? `registered ${c.used_at.slice(0, 10)}` : "unused"}</td>
                      <td>{c.participant_id ?? "—"}</td>
                      <td>
                        {c.used_by_user_id != null ? (
                          <button
                            className="btn-ghost btn-sm"
                            disabled={codeBusy}
                            onClick={() => void issueResetLink(c.used_by_user_id as number)}
                          >
                            Generate reset link
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No study IDs loaded for this substudy yet.</p>
            )}
          </>
        ) : null}
      </section>

      <section className="app-card">
        <h2>Protocols</h2>
        <p className="muted">
          A substudy&apos;s protocol configures its participant portal: capture methods, photo
          counts, results visibility, and day-based instructions. Versions save as
          inactive; activating one makes it live immediately. Substudies with no active protocol use the
          public default.
        </p>
        <div className="field-grid">
          <label className="field">
            <span>Substudy to configure</span>
            <select
              value={protoSubstudy}
              onChange={(e) => {
                setProtoSubstudy(e.target.value);
                void loadProtocols(e.target.value, true);
              }}
            >
              <option value="">Select…</option>
              {substudies.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </label>
        </div>
        {protoSubstudy ? (
          <>
            {protoList.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {protoList.map((p) => (
                    <tr key={p.id}>
                      <td>v{p.version}</td>
                      <td>{p.active ? "active" : "inactive"}</td>
                      <td>{p.created_at.slice(0, 10)}</td>
                      <td>
                        <button
                          className="btn-ghost btn-sm"
                          disabled={protoBusy}
                          onClick={() => setProtoJson(JSON.stringify(p.config, null, 2))}
                        >
                          Edit
                        </button>{" "}
                        {!p.active ? (
                          <button
                            className="btn-ghost btn-sm"
                            disabled={protoBusy}
                            onClick={() => void activateProtocol(p.id)}
                          >
                            Activate
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No protocol versions yet; this substudy uses the default.</p>
            )}
            <form className="q-form" onSubmit={saveProtocol}>
              <label className="field">
                <span>
                  Protocol config (JSON). Edit and save as a new version; versions are
                  immutable
                </span>
                <textarea
                  rows={8}
                  value={protoJson}
                  onChange={(e) => setProtoJson(e.target.value)}
                  placeholder={
                    '{"photo": {"methods": ["webcam"], "min_webcam_photos": 10, "max_photos": 10, "weekly_photo_limit": 70}, "results_visible": false, "instructions": [{"from_day": 0, "title": "Welcome", "markdown": "Capture 10 webcam photos each day."}]}'
                  }
                />
              </label>
              <p>
                <button
                  className="btn-ghost btn-sm"
                  type="submit"
                  disabled={protoBusy || !protoJson.trim()}
                >
                  Save as new version
                </button>
              </p>
            </form>
          </>
        ) : null}
        {protoNotice ? <p className="muted">{protoNotice}</p> : null}
        {protoError ? <p className="form-error">{protoError}</p> : null}
      </section>

      <ConsentVersionsCard substudies={substudies} />
    </TabPanel>
  );
}
